import "@/App.css";
import { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import WebAnalytics from "@/analytics/WebAnalytics";
import { savePostAuthRedirect } from "@/lib/postAuthRedirect";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/hooks/use-theme";
import { AuthProvider, useAuth } from "@/hooks/use-auth";
import { lazyRetry } from "@/lib/lazyRetry";
import { PageLoader } from "@/components/PageLoader";
import { RouteErrorBoundary } from "@/components/AppErrorBoundary";
import SignIn from "@/pages/SignIn";
import AppShell from "@/components/layout/AppShell";
import { MessagingProvider } from "@/hooks/messaging-provider";
import AdminShell from "@/components/layout/AdminShell";
import { ImpersonationBanner } from "@/components/layout/ImpersonationBanner";
import UpdateBanner from "@/components/UpdateBanner";
import ConnectionBanner from "@/components/ConnectionBanner";
import ReconnectScreen from "@/components/ReconnectScreen";
import CanopyToastListener from "@/components/canopy/CanopyToastListener";
import AppInstallBanner from "@/components/AppInstallBanner";
import PublicOrPortal from "@/components/PublicOrPortal";

// Auth callbacks
const GoogleAuthCallback = lazyRetry(() => import("@/pages/GoogleAuthCallback"));
const MagicLinkCallback = lazyRetry(() => import("@/pages/MagicLinkCallback"));
const LinkedInCallback = lazyRetry(() => import("@/pages/LinkedInCallback"));
// Public
const FounderShare = lazyRetry(() => import("@/pages/FounderShare"));
const PublicVenture = lazyRetry(() => import("@/pages/PublicVenture"));
const DemoDay = lazyRetry(() => import("@/pages/DemoDay"));
const Vault = lazyRetry(() => import("@/pages/Vault"));
const VaultShare = lazyRetry(() => import("@/pages/VaultShare"));
const PublicEvent = lazyRetry(() => import("@/pages/PublicEvent"));
const PublicEvents = lazyRetry(() => import("@/pages/PublicEvents"));
const PublicDiscover = lazyRetry(() => import("@/pages/PublicDiscover"));
const PublicDiscoverList = lazyRetry(() => import("@/pages/PublicDiscoverList"));
// Member workspace
const Dashboard = lazyRetry(() => import("@/pages/Dashboard"));
const Venture = lazyRetry(() => import("@/pages/Venture"));
const VentureExplore = lazyRetry(() => import("@/pages/VentureExplore"));
const VentureProfilePage = lazyRetry(() => import("@/pages/VentureProfilePage"));
const SessionDetail = lazyRetry(() => import("@/pages/SessionDetail"));
const MeetingRoom = lazyRetry(() => import("@/pages/MeetingRoom"));
const GuestMeetingRoom = lazyRetry(() => import("@/pages/GuestMeetingRoom"));
const MeetingRecordings = lazyRetry(() => import("@/pages/MeetingRecordings"));
const PitchDeck = lazyRetry(() => import("@/pages/PitchDeck"));
const ProductPreview = lazyRetry(() => import("@/pages/ProductPreview"));
const Mentors = lazyRetry(() => import("@/pages/Mentors"));
const Community = lazyRetry(() => import("@/pages/Community"));
const Messages = lazyRetry(() => import("@/pages/Messages"));
const CommunityGuidelines = lazyRetry(() => import("@/pages/CommunityGuidelines"));
const PrivacySafety = lazyRetry(() => import("@/pages/PrivacySafety"));
const AIPrivacy = lazyRetry(() => import("@/pages/AIPrivacy"));
const SessionsDevices = lazyRetry(() => import("@/pages/SessionsDevices"));
const MemberProfile = lazyRetry(() => import("@/pages/MemberProfile"));
const UserProfile = lazyRetry(() => import("@/pages/UserProfile"));
const Copilot = lazyRetry(() => import("@/pages/Copilot"));
const GetApp = lazyRetry(() => import("@/pages/GetApp"));
const Journal = lazyRetry(() => import("@/pages/Journal"));
const Events = lazyRetry(() => import("@/pages/Events"));
const Learning = lazyRetry(() => import("@/pages/Learning"));
const Canopy = lazyRetry(() => import("@/pages/Canopy"));
const FounderIntelligence = lazyRetry(() => import("@/pages/FounderIntelligence"));
const Investors = lazyRetry(() => import("@/pages/Investors"));
const Match = lazyRetry(() => import("@/pages/Match"));
const Network = lazyRetry(() => import("@/pages/Network"));
const CRM = lazyRetry(() => import("@/pages/CRM"));
const Internships = lazyRetry(() => import("@/pages/Internships"));
const ParentPortal = lazyRetry(() => import("@/pages/ParentPortal"));
const UserConsole = lazyRetry(() => import("@/pages/UserConsole"));
const ApplyCohort = lazyRetry(() => import("@/pages/ApplyCohort"));
const Discover = lazyRetry(() => import("@/pages/Discover"));
const DiscoverDetail = lazyRetry(() => import("@/pages/DiscoverDetail"));
const Marketplace = lazyRetry(() => import("@/pages/Marketplace"));
const Capital = lazyRetry(() => import("@/pages/Capital"));
const CapitalPublic = lazyRetry(() => import("@/pages/CapitalPublic"));
const PublicMentorShell = lazyRetry(() => import("@/pages/PublicMentorShell"));
const CapitalApply = lazyRetry(() => import("@/pages/CapitalApply"));
const CapitalStatus = lazyRetry(() => import("@/pages/CapitalStatus"));
const CapitalProgramPage = lazyRetry(() => import("@/pages/CapitalProgramPage"));
const CapitalEvaluate = lazyRetry(() => import("@/pages/CapitalEvaluate"));
const AdminCapital = lazyRetry(() => import("@/pages/admin/AdminCapital"));
const Family = lazyRetry(() => import("@/pages/Family"));
const Alumni = lazyRetry(() => import("@/pages/Alumni"));
const Settings = lazyRetry(() => import("@/pages/Settings"));
const LessonPlayer = lazyRetry(() => import("@/pages/LessonPlayer"));
const CourseOutline = lazyRetry(() => import("@/pages/CourseOutline"));
const BecomeMentor = lazyRetry(() => import("@/pages/BecomeMentor"));
const AdminMarketplace = lazyRetry(() => import("@/pages/admin/AdminMarketplace"));
const Calendar = lazyRetry(() => import("@/pages/Calendar"));
const EventDetail = lazyRetry(() => import("@/pages/EventDetail"));
const MentorDetail = lazyRetry(() => import("@/pages/MentorDetail"));
const DealDetail = lazyRetry(() => import("@/pages/DealDetail"));
const InvestorDetail = lazyRetry(() => import("@/pages/InvestorDetail"));
const InvestorProfilePage = lazyRetry(() => import("@/pages/InvestorProfilePage"));
const InternshipDetail = lazyRetry(() => import("@/pages/InternshipDetail"));
const MarketplaceDetail = lazyRetry(() => import("@/pages/MarketplaceDetail"));
const AlumniProfile = lazyRetry(() => import("@/pages/AlumniProfile"));
const FamilyStory = lazyRetry(() => import("@/pages/FamilyStory"));
// Persona homes
const MentorHome = lazyRetry(() => import("@/pages/MentorHome"));
const MentorMentees = lazyRetry(() => import("@/pages/MentorMentees"));
const MentorFinance = lazyRetry(() => import("@/pages/MentorFinance"));
const MentorCheckin = lazyRetry(() => import("@/pages/MentorCheckin"));
const MentorAttendance = lazyRetry(() => import("@/pages/MentorAttendance"));
const MentorAnalytics = lazyRetry(() => import("@/pages/MentorAnalytics"));
const MentorSubmissions = lazyRetry(() => import("@/pages/MentorSubmissions"));
const InvestorHome = lazyRetry(() => import("@/pages/InvestorHome"));
const AlumniHome = lazyRetry(() => import("@/pages/AlumniHome"));
const PartnerHome = lazyRetry(() => import("@/pages/PartnerHome"));
// Admin workspace
const AdminDashboard = lazyRetry(() => import("@/pages/admin/AdminDashboard"));
const AdminUsers = lazyRetry(() => import("@/pages/admin/AdminUsers"));
const AdminApplications = lazyRetry(() => import("@/pages/admin/AdminApplications"));
const AdminPrograms = lazyRetry(() => import("@/pages/admin/AdminPrograms"));
const CohortDetail = lazyRetry(() => import("@/pages/admin/programs/CohortDetail"));
const AdminAI = lazyRetry(() => import("@/pages/admin/AdminAI"));
const AdminLMS = lazyRetry(() => import("@/pages/admin/AdminLMS"));
const AdminCourseBuilder = lazyRetry(() => import("@/pages/admin/AdminCourseBuilder"));
const AdminFinance = lazyRetry(() => import("@/pages/admin/AdminFinance"));
const AdminVendors = lazyRetry(() => import("@/pages/admin/AdminVendors"));
const AdminReports = lazyRetry(() => import("@/pages/admin/AdminReports"));
const AdminRatings = lazyRetry(() => import("@/pages/admin/AdminRatings"));
const AdminSessions = lazyRetry(() => import("@/pages/admin/AdminSessions"));
const AdminSessionAttendance = lazyRetry(() => import("@/pages/admin/AdminSessionAttendance"));
const AdminMentors = lazyRetry(() => import("@/pages/admin/AdminMentors"));
// AdminMentorAccess merged into AdminMentors — legacy `/app/admin/mentor-access`
// route now redirects to `/app/admin/mentors` (see route below).
const AdminMentorFinance = lazyRetry(() => import("@/pages/admin/AdminMentorFinance"));
const AdminEvents = lazyRetry(() => import("@/pages/admin/AdminEvents"));
const AdminEventForm = lazyRetry(() => import("@/pages/admin/AdminEventForm"));
const AdminEventRsvps = lazyRetry(() => import("@/pages/admin/AdminEventRsvps"));
const AdminSubmissions = lazyRetry(() => import("@/pages/admin/AdminSubmissions"));
const AdminInvestors = lazyRetry(() => import("@/pages/admin/AdminInvestors"));
const AdminStartups = lazyRetry(() => import("@/pages/admin/AdminVentures"));
const AdminVentureDetail = lazyRetry(() => import("@/pages/admin/AdminVentureDetail"));
const AdminCareer = lazyRetry(() => import("@/pages/admin/AdminCareer"));
const AdminMatch = lazyRetry(() => import("@/pages/admin/AdminMatch"));
const AdminCommunity = lazyRetry(() => import("@/pages/admin/AdminCommunity"));
const AdminMessaging = lazyRetry(() => import("@/pages/admin/AdminMessaging"));
const AdminRBAC = lazyRetry(() => import("@/pages/admin/AdminRBAC"));
const AdminSettings = lazyRetry(() => import("@/pages/admin/AdminSettings"));
const AdminSafety = lazyRetry(() => import("@/pages/admin/AdminSafety"));
const AdminComms = lazyRetry(() => import("@/pages/admin/AdminComms"));
const AdminCommunicationHub = lazyRetry(() => import("@/pages/admin/AdminCommunicationHub"));
const NotificationPreferences = lazyRetry(() => import("@/pages/NotificationPreferences"));
const AdminSecurityOps = lazyRetry(() => import("@/pages/admin/AdminSecurityOps"));
const AdminEngineering = lazyRetry(() => import("@/pages/admin/AdminEngineering"));
const AdminDiscover = lazyRetry(() => import("@/pages/admin/AdminDiscover"));
const AdminDiscoverEditor = lazyRetry(() => import("@/pages/admin/AdminDiscoverEditor"));

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Protects /app/* — redirects unauth users to /signin, remembering the page
// they wanted (shared links resume there after sign-in).
const RequireAuth = ({ children }) => {
  const { user, loading, authStatus } = useAuth();
  const location = useLocation();
  if (loading) return <PageLoader full />;
  // Credentials present but unconfirmed (offline / deploy-time 503): never
  // grant access on stale state, never sign out — hold with a retry screen.
  if (authStatus === 'reconnecting') return <ReconnectScreen />;
  if (!user) {
    savePostAuthRedirect(location.pathname + location.search);
    return <Navigate to="/signin" replace />;
  }
  return children;
};

// Gates the Admin workspace to admin-persona users only. Mentors use the
// Mentor Portal shell (AppShell) with their own routes like `/app/mentor-lms`
// — they never enter the Admin Console.
const RequireAdmin = ({ children }) => {
  const { user } = useAuth();
  if (user?.persona !== 'admin') return <Navigate to="/app" replace />;
  return children;
};

// /app entry — every non-student persona lands on its own console home.
// Admins intentionally keep the member Dashboard here so the Admin Console's
// "Back to Founder OS" link still reaches the member workspace.
const PERSONA_APP_HOME = {
  user: '/app/user-home', mentor: '/app/mentor-home', alumni: '/app/alumni-home',
  investor: '/app/investor-home', parent: '/app/parent', partner: '/app/partner-home',
};
const HomeRoute = () => {
  const { user } = useAuth();
  const target = PERSONA_APP_HOME[user?.persona];
  if (target) return <Navigate to={target} replace />;
  return <Dashboard />;
};

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <ThemeProvider>
        <AuthProvider>
          <MessagingProvider>
          <div className="App">
            <BrowserRouter>
              <WebAnalytics />
              <ImpersonationBanner />
              <UpdateBanner />
              <ConnectionBanner />
              <AppInstallBanner />
              <RouteErrorBoundary>
              <Suspense fallback={<PageLoader full />}>
              <Routes>
                <Route path="/" element={<Navigate to="/signin" replace />} />
                <Route path="/get" element={<GetApp />} />
                <Route path="/download" element={<GetApp />} />
                <Route path="/signin" element={<SignIn />} />
                <Route path="/auth/google" element={<GoogleAuthCallback />} />
                <Route path="/auth/linkedin" element={<LinkedInCallback />} />
                <Route path="/auth/magic" element={<MagicLinkCallback />} />
                <Route path="/founder/:slug" element={<FounderShare />} />
                <Route path="/v/:slug" element={<PublicVenture />} />
                <Route path="/demo-day/:slug" element={<DemoDay />} />
                <Route path="/capital" element={<CapitalPublic />} />
                <Route path="/mentors" element={
                  <PublicOrPortal portalPathBuilder={() => '/app/mentors'}>
                    <PublicMentorShell><Mentors /></PublicMentorShell>
                  </PublicOrPortal>
                } />
                <Route path="/mentors/apply" element={<PublicMentorShell><BecomeMentor /></PublicMentorShell>} />
                <Route path="/mentors/:mentorId" element={
                  <PublicOrPortal portalPathBuilder={(p) => `/app/mentors/${p.mentorId}`}>
                    <PublicMentorShell><MentorDetail /></PublicMentorShell>
                  </PublicOrPortal>
                } />
                <Route path="/share/vault/:token" element={<VaultShare />} />
                <Route path="/e/:eventId" element={
                  <PublicOrPortal portalPathBuilder={(p) => `/app/events/${p.eventId}`}>
                    <PublicEvent />
                  </PublicOrPortal>
                } />
                <Route path="/events" element={<PublicEvents />} />
                <Route path="/discover" element={
                  <PublicOrPortal portalPathBuilder={() => '/app/discover'}>
                    <PublicDiscoverList />
                  </PublicOrPortal>
                } />
                <Route path="/discover/:slug" element={
                  <PublicOrPortal portalPathBuilder={(p) => `/app/discover/${p.slug}`}>
                    <PublicDiscover />
                  </PublicOrPortal>
                } />
                <Route path="/community-guidelines" element={<CommunityGuidelines />} />
                <Route path="/guest/meet/event/:refId" element={<GuestMeetingRoom kind="event" />} />
                <Route path="/guest/meet/interview/:refId" element={<GuestMeetingRoom kind="interview" />} />
                <Route path="/evaluate/capital" element={<CapitalEvaluate />} />
                <Route element={<RequireAuth><AppShell /></RequireAuth>}>
              <Route path="/app" element={<HomeRoute />} />
              <Route path="/app/user-home" element={<UserConsole />} />
              <Route path="/app/apply" element={<ApplyCohort />} />
              <Route path="/app/mentor-home" element={<MentorHome />} />
              <Route path="/app/mentor-sessions" element={<Navigate to="/app/mentor-checkin" replace />} />
              <Route path="/app/mentor-mentees" element={<MentorMentees />} />
              <Route path="/app/mentor-finance" element={<MentorFinance />} />
              <Route path="/app/mentor-checkin" element={<MentorCheckin />} />
              <Route path="/app/mentor-attendance" element={<MentorAttendance />} />
              <Route path="/app/mentor-analytics" element={<MentorAnalytics />} />
              <Route path="/app/mentor-submissions" element={<MentorSubmissions />} />
              {/* LMS in Mentor Portal shell — keeps the mentor sidebar visible
                  while rendering the LMS admin content. Backend RBAC scopes
                  what the mentor can see/edit (per admin_lms._mentor_scope). */}
              <Route path="/app/mentor-lms" element={<AdminLMS />} />
              <Route path="/app/mentor-lms/:courseId" element={<AdminCourseBuilder />} />
              <Route path="/app/mentor-bookings" element={<Navigate to="/app/mentor-checkin" replace />} />
              <Route path="/app/admin-home" element={<Navigate to="/app/admin" replace />} />
              <Route path="/app/investor-home" element={<InvestorHome />} />
              <Route path="/app/alumni-home" element={<AlumniHome />} />
              <Route path="/app/partner-home" element={<PartnerHome />} />
              <Route path="/app/settings" element={<Settings />} />
              <Route path="/app/meet/:kind/:refId" element={<MeetingRoom />} />
              <Route path="/app/meet/:kind/:refId/recordings" element={<MeetingRecordings />} />
              <Route path="/app/settings/privacy-safety" element={<PrivacySafety />} />
              <Route path="/app/settings/ai-privacy" element={<AIPrivacy />} />
              <Route path="/app/settings/sessions" element={<SessionsDevices />} />
              <Route path="/app/venture" element={<Venture />} />
              <Route path="/app/vault" element={<Vault />} />
              <Route path="/app/ventures/explore" element={<VentureExplore />} />
              <Route path="/app/ventures/explore/:slug" element={<VentureProfilePage />} />
              <Route path="/app/sessions/:id" element={<SessionDetail />} />
              <Route path="/app/venture/pitch" element={<PitchDeck />} />
              <Route path="/app/venture/product" element={<ProductPreview />} />
              <Route path="/app/mentors" element={<Mentors />} />
              <Route path="/app/mentors/apply" element={<BecomeMentor />} />
              <Route path="/app/mentors/:mentorId" element={<MentorDetail />} />
              <Route path="/app/community" element={<Community />} />
              <Route path="/app/messages/*" element={<Messages />} />
              <Route path="/app/member/:id" element={<MemberProfile />} />
              <Route path="/app/u/:id" element={<UserProfile />} />
              <Route path="/app/profile/:id" element={<UserProfile />} />
              <Route path="/app/copilot" element={<Copilot />} />
              <Route path="/app/journal" element={<Journal />} />
              <Route path="/app/events" element={<Events />} />
              <Route path="/app/events/:eventId" element={<EventDetail />} />
              <Route path="/app/calendar" element={<Calendar />} />
              <Route path="/app/learn" element={<Learning />} />
              <Route path="/app/learn/:courseId" element={<LessonPlayer />} />
              <Route path="/app/learn/:courseId/outline" element={<CourseOutline />} />
              <Route path="/app/score" element={<Canopy />} />
              <Route path="/app/canopy" element={<Canopy />} />
              <Route path="/app/intelligence" element={<FounderIntelligence />} />
              <Route path="/app/investors" element={<Investors />} />
              <Route path="/app/match" element={<Match />} />
              <Route path="/app/network" element={<Network />} />
              <Route path="/app/crm" element={<CRM />} />
              <Route path="/app/internships" element={<Internships />} />
              <Route path="/app/parent" element={<ParentPortal />} />
              <Route path="/app/marketplace" element={<Marketplace />} />
              <Route path="/app/capital" element={<Capital />} />
              <Route path="/app/capital/apply" element={<CapitalApply />} />
              <Route path="/app/capital/application" element={<CapitalStatus />} />
              <Route path="/app/capital/program" element={<CapitalProgramPage />} />
              <Route path="/app/global" element={<Navigate to="/app" replace />} />
              <Route path="/app/family" element={<Family />} />
              <Route path="/app/alumni" element={<Alumni />} />
              <Route path="/app/crm/:dealId" element={<DealDetail />} />
              <Route path="/app/investors/directory/:profileId" element={<InvestorProfilePage />} />
              <Route path="/app/investors/:investorId" element={<InvestorDetail />} />
              <Route path="/app/internships/:listingId" element={<InternshipDetail />} />
              <Route path="/app/marketplace/:productId" element={<MarketplaceDetail />} />
              <Route path="/app/alumni/:alumniId" element={<AlumniProfile />} />
              <Route path="/app/family/:heirId" element={<FamilyStory />} />
              {/* Discover — Opportunity Intelligence Engine */}
              <Route path="/app/discover" element={<Discover />} />
              <Route path="/app/discover/:slug" element={<DiscoverDetail />} />
              <Route path="/app/settings/notifications" element={<NotificationPreferences />} />
            </Route>
            <Route element={<RequireAuth><RequireAdmin><AdminShell /></RequireAdmin></RequireAuth>}>
              <Route path="/app/admin" element={<AdminDashboard />} />
              <Route path="/app/admin/users" element={<AdminUsers />} />
              <Route path="/app/admin/applications" element={<AdminApplications />} />
              <Route path="/app/admin/programs" element={<AdminPrograms />} />
              <Route path="/app/admin/programs/cohorts/:cohortId" element={<CohortDetail />} />
              <Route path="/app/admin/cohorts" element={<Navigate to="/app/admin/programs" replace />} />
              <Route path="/app/admin/ai" element={<AdminAI />} />
              <Route path="/app/admin/lms" element={<AdminLMS />} />
              <Route path="/app/admin/lms/submissions" element={<AdminSubmissions />} />
              <Route path="/app/admin/sessions" element={<AdminSessions />} />
              <Route path="/app/admin/sessions/:id/attendance" element={<AdminSessionAttendance />} />
              <Route path="/app/admin/mentors" element={<AdminMentors />} />
              <Route path="/app/admin/mentor-access" element={<Navigate to="/app/admin/mentors" replace />} />
              <Route path="/app/admin/mentor-finance" element={<AdminMentorFinance />} />
              <Route path="/app/admin/marketplace" element={<AdminMarketplace />} />
              <Route path="/app/admin/events" element={<AdminEvents />} />
              <Route path="/app/admin/events/new" element={<AdminEventForm />} />
              <Route path="/app/admin/events/:id/edit" element={<AdminEventForm />} />
              <Route path="/app/admin/events/:id/rsvps" element={<AdminEventRsvps />} />
              <Route path="/app/admin/investors" element={<AdminInvestors />} />
              <Route path="/app/admin/capital" element={<AdminCapital />} />
              <Route path="/app/admin/startups" element={<AdminStartups />} />
              <Route path="/app/admin/startups/:id" element={<AdminVentureDetail />} />
              <Route path="/app/admin/career" element={<AdminCareer />} />
              <Route path="/app/admin/match" element={<AdminMatch />} />
              <Route path="/app/admin/community" element={<AdminCommunity />} />
              <Route path="/app/admin/messaging" element={<AdminMessaging />} />
              <Route path="/app/admin/rbac" element={<AdminRBAC />} />
              <Route path="/app/admin/settings" element={<AdminSettings />} />
              <Route path="/app/admin/safety" element={<AdminSafety />} />
              <Route path="/app/admin/communications" element={<AdminCommunicationHub />} />
              <Route path="/app/admin/communications/broadcast" element={<AdminComms />} />
              <Route path="/app/admin/notifications" element={<Navigate to="/app/admin/communications" replace />} />
              <Route path="/app/admin/faculty" element={<Navigate to="/app/admin/mentors" replace />} />
              <Route path="/app/admin/lms/:courseId" element={<AdminCourseBuilder />} />
              <Route path="/app/admin/finance" element={<AdminFinance />} />
              <Route path="/app/admin/vendors" element={<AdminVendors />} />
              <Route path="/app/admin/reports" element={<AdminReports />} />
              <Route path="/app/admin/ratings" element={<AdminRatings />} />
              <Route path="/app/admin/security" element={<AdminSecurityOps />} />
              <Route path="/app/admin/engineering" element={<AdminEngineering />} />
              <Route path="/app/admin/discover" element={<AdminDiscover />} />
              <Route path="/app/admin/discover/new" element={<AdminDiscoverEditor />} />
              <Route path="/app/admin/discover/:id" element={<AdminDiscoverEditor />} />
            </Route>
            <Route path="*" element={<Navigate to="/signin" replace />} />
            </Routes>
              </Suspense>
              </RouteErrorBoundary>
            </BrowserRouter>
          <Toaster position="top-right" />
          <CanopyToastListener />
        </div>
        </MessagingProvider>
      </AuthProvider>
    </ThemeProvider>
  </GoogleOAuthProvider>
  );
}

export default App;
